import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sun, Users, DollarSign, TrendingUp, Landmark, FileText } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar } from 'recharts';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { api } from '../lib/api';
import { PageHeader, SectionHeader, RichSection } from '../components/Shared';
import { MoreDetails } from '../components/MoreDetails';
import { FactBadge, FactCard, SourceCite, PositionBox, QuestionBox, FaqList } from '../components/FactCheck';
import { SourceLinkCard } from '../components/DataSources';
import { NextStepBand } from '../components/NextStepBand';

const fmtMoney = (n) => '$' + Number(n || 0).toLocaleString();
const fmtMoneyShort = (n) => (n >= 1000000 ? '$' + (n / 1000000).toFixed(1) + 'M' : '$' + Math.round(n / 1000) + 'K');

const FAQS = [
  { q: 'What is the Sunshine List?', a: 'Every year the Government of Ontario publishes the names, positions, and salaries of public sector employees paid $100,000 or more in the previous calendar year. That is the provincial public salary disclosure threshold. City of Kawartha Lakes staff appear when their pay meets it.' },
  { q: 'Does being on the list mean someone is overpaid?', a: 'No. Appearing on the Sunshine List does not automatically mean an employee is overpaid. It is a public record, not an accusation. Many listed roles, like fire, paramedics, and police, involve demanding front-line work.' },
  { q: 'Why do the numbers keep growing?', a: 'Partly because the $100,000 threshold has never been adjusted for inflation since 1996, so normal wage growth moves more people over the line every year. Rising counts alone do not prove waste. The fair questions are about overall cost growth, management structure, and value for taxpayers.' },
  { q: 'Where does this data come from?', a: 'From the Government of Ontario\'s official Public Sector Salary Disclosure. Every figure on this page links back to official records, and the methodology is shown so you can check it yourself.' },
];

const StatCard = ({ icon: Icon, label, value, sub, testId }) => (
  <Card data-testid={testId}>
    <CardContent className="p-5">
      <div className="flex items-center gap-2 mb-1.5">
        <Icon className="h-4 w-4 text-[#24406f]" />
        <p className="text-[11px] font-black uppercase tracking-wider text-slate-500">{label}</p>
      </div>
      <p className="text-2xl sm:text-3xl font-black text-[#1a2744]">{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </CardContent>
  </Card>
);

export default function SunshineList() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/sunshine-list-data').then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const latest = data?.latest;
  const years = data?.years || [];
  const comparison = data?.comparison;
  const growthPct = comparison ? Math.round(((comparison.to.count - comparison.from.count) / comparison.from.count) * 100) : null;

  return (
    <div>
      <PageHeader
        pageKey="sunshine-list"
        kicker="YOUR MONEY"
        title="The Sunshine List, explained"
        subtitle="Ontario's public salary disclosure for the City of Kawartha Lakes: what it is, what the numbers show, and the fair questions taxpayers can ask."
      />
      <RichSection id="sunshine-sections.context_intro" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8" />

      {/* What it is + fairness, up front */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14" data-testid="sunshine-what-section">
        <SectionHeader kicker="PLAIN LANGUAGE" title="What is the Sunshine List?" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FactCard kind="verified_fact" title="$100,000 is the provincial public salary disclosure threshold"
            cite={{ source: 'Public Sector Salary Disclosure Act, 1996 (Ontario)', year: '1996', accessed: data?.accessed_date || '2026', url: 'https://www.ontario.ca/laws/statute/96p01a' }}>
            Every year, Ontario publishes the names, positions, and salaries of public sector employees paid $100,000 or more in the previous calendar year. Municipal employees, including City of Kawartha Lakes staff, appear when their pay meets the threshold.
          </FactCard>
          <FactCard kind="verified_fact" title="Being on the list does not automatically mean someone is overpaid">
            The Sunshine List is a public record, not an accusation. Many listed roles involve demanding front-line and professional work. This page exists for transparency about the overall cost and structure of City government, not to attack employees.
          </FactCard>
        </div>
      </section>

      {/* Dashboard */}
      <section className="bg-white border-y border-slate-200 py-10 md:py-14" data-testid="sunshine-dashboard-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader kicker="THE DASHBOARD" title="Kawartha Lakes disclosure, by the numbers" dek={`Compiled from official Ontario disclosure records. Latest year: ${latest?.year || ''}.`} />
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4"><Skeleton className="h-28" /><Skeleton className="h-28" /><Skeleton className="h-28" /><Skeleton className="h-28" /><Skeleton className="h-28" /></div>
          ) : !data?.available ? (
            <p className="text-slate-500">The dashboard is being prepared. See the official sources below.</p>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="sunshine-stat-cards">
                <StatCard icon={Users} label={`Disclosed employees (${latest.year})`} value={latest.count.toLocaleString()} testId="sunshine-stat-count" />
                <StatCard icon={DollarSign} label="Total disclosed salaries" value={fmtMoneyShort(latest.total)} sub={fmtMoney(latest.total)} testId="sunshine-stat-total" />
                <StatCard icon={DollarSign} label="Average disclosed salary" value={fmtMoney(latest.average)} testId="sunshine-stat-average" />
                <StatCard icon={TrendingUp} label="Paid $150,000 or more" value={latest.over_150k} testId="sunshine-stat-150k" />
                <StatCard icon={TrendingUp} label="Paid $200,000 or more" value={latest.over_200k} testId="sunshine-stat-200k" />
              </div>

              {/* Simple first: key numbers above. More... opens charts and detail tables. */}
              <div className="mt-6">
              <MoreDetails testId="sunshine-dashboard-more">
              {/* Trend charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-2">
                <Card data-testid="sunshine-trend-count">
                  <CardContent className="p-5">
                    <p className="font-serif font-bold text-[#1a2744] mb-3">Disclosed employees by year</p>
                    <div className="h-56">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={years} margin={{ top: 5, right: 10, bottom: 0, left: -10 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="year" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                          <YAxis tick={{ fontSize: 11 }} />
                          <Tooltip formatter={(v) => [v, 'Employees']} />
                          <Line type="monotone" dataKey="count" stroke="#1a2744" strokeWidth={2.5} dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
                <Card data-testid="sunshine-trend-total">
                  <CardContent className="p-5">
                    <p className="font-serif font-bold text-[#1a2744] mb-3">Total disclosed salaries by year</p>
                    <div className="h-56">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={years} margin={{ top: 5, right: 10, bottom: 0, left: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="year" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                          <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtMoneyShort} />
                          <Tooltip formatter={(v) => [fmtMoney(v), 'Total salaries']} />
                          <Bar dataKey="total" fill="#C9A84C" radius={[3, 3, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* 2015 vs 2025 */}
              {comparison && (
                <Card className="mt-6" data-testid="sunshine-comparison">
                  <CardContent className="p-5 sm:p-6">
                    <div className="flex flex-wrap items-center gap-2">
                      <FactBadge kind="verified_fact" />
                      <p className="font-serif font-bold text-[#1a2744]">{comparison.from.year} versus {comparison.to.year}</p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                      <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                        <p className="text-xs font-black uppercase tracking-wider text-slate-500">{comparison.from.year}</p>
                        <p className="text-xl font-black text-[#1a2744] mt-1">{comparison.from.count} disclosed</p>
                        <p className="text-sm text-slate-600">{fmtMoney(comparison.from.total)} total</p>
                      </div>
                      <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                        <p className="text-xs font-black uppercase tracking-wider text-slate-500">{comparison.to.year}</p>
                        <p className="text-xl font-black text-[#1a2744] mt-1">{comparison.to.count} disclosed</p>
                        <p className="text-sm text-slate-600">{fmtMoney(comparison.to.total)} total</p>
                      </div>
                      <div className="rounded-xl bg-[#eaf2ff] border border-[#24406f]/30 p-4">
                        <p className="text-xs font-black uppercase tracking-wider text-[#24406f]">Change</p>
                        <p className="text-xl font-black text-[#1a2744] mt-1" data-testid="sunshine-growth-pct">+{growthPct}% employees</p>
                        <p className="text-sm text-slate-600">in ten years</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Top disclosed positions */}
              {latest?.top_earners?.length > 0 && (
                <Card className="mt-6" data-testid="sunshine-top-earners">
                  <CardContent className="p-5 sm:p-6">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <FactBadge kind="official_source" />
                      <p className="font-serif font-bold text-[#1a2744]">Highest disclosed City positions ({latest.year})</p>
                    </div>
                    <p className="text-xs text-slate-500 mb-3">Positions and senior management roles as disclosed. Shown by position to keep the focus on structure and cost, not individuals. Names are available in the official disclosure.</p>
                    <div className="divide-y divide-slate-100">
                      {latest.top_earners.map((t, i) => (
                        <div key={`${t.position}-${t.salary}-${i}`} className="flex items-center justify-between gap-3 py-2.5" data-testid="sunshine-top-earner-row">
                          <p className="text-sm font-semibold text-[#1a2744]">{t.position}</p>
                          <p className="text-sm font-black text-[#1a2744]">{fmtMoney(t.salary)}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
              </MoreDetails>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Context: the honest part */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14" data-testid="sunshine-context-section">
        <SectionHeader kicker="THE HONEST CONTEXT" title="Before drawing conclusions, read this" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FactCard kind="verified_fact" title="The $100,000 threshold has stayed the same since 1996"
            cite={{ source: 'Public Sector Salary Disclosure Act, 1996 (Ontario)', year: '1996', accessed: data?.accessed_date || '2026', url: 'https://www.ontario.ca/laws/statute/96p01a' }}>
            The disclosure threshold has never been adjusted for inflation. Normal wage growth moves more people over the line every year, in every municipality. That means rising Sunshine List counts alone do not prove waste or mismanagement.
          </FactCard>
          <Card data-testid="sunshine-concern-card">
            <CardContent className="p-5">
              <FactBadge kind="steves_position" />
              <p className="mt-2.5 font-serif font-bold text-[#1a2744] leading-snug">Taxpayers still have the right to ask whether the overall cost of government is growing faster than it should.</p>
              <p className="mt-1.5 text-sm text-slate-700 leading-relaxed">Steve's concern is not any individual employee. It is overall compensation growth, management costs, staffing trends, value for taxpayers, and whether City government is structured efficiently.</p>
            </CardContent>
          </Card>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          <QuestionBox testId="sunshine-question-1">Is the overall cost of City government growing faster than the population and the services residents receive?</QuestionBox>
          <QuestionBox testId="sunshine-question-2">How much of the growth is front-line service delivery, and how much is management and administration?</QuestionBox>
        </div>
        <div className="mt-6">
          <PositionBox title="Transparency, not attacks." testId="sunshine-position-band">
            <p>This page will keep growing with sourced analysis of compensation trends, management costs, and structure. Everything is checked against official records before it is published as fact, and Steve's interpretations are always labelled as his position.</p>
            <div className="mt-4 flex flex-col sm:flex-row gap-3">
              <Button asChild className="h-11 bg-white hover:bg-slate-100 text-[#1a2744] font-bold btn-press" data-testid="sunshine-cao-button"><Link to="/cao-review">The CAO Review <ArrowRight className="h-4 w-4 ml-1" /></Link></Button>
              <Button asChild variant="outline" className="h-11 border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white font-bold btn-press" data-testid="sunshine-finances-button"><Link to="/city-finances">City Finances, Explained</Link></Button>
            </div>
          </PositionBox>
        </div>
      </section>

      {/* Sources + methodology */}
      <section className="bg-white border-y border-slate-200 py-10 md:py-14" data-testid="sunshine-sources-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader kicker="CHECK IT YOURSELF" title="Sources and methodology" dek="Every number above traces back to the official disclosure. Follow the links and verify." />
          <MoreDetails testId="sunshine-sources-more">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {(data?.sources || []).map((s) => (
              <SourceLinkCard key={s.url} icon={Sun} title={s.label} body={s.year ? `Record year: ${s.year}` : ''} url={s.url} label="View the official source" />
            ))}
          </div>
          {data?.methodology && (
            <div className="mt-5">
              <SourceCite source="Compiled dataset for this dashboard" year={String(latest?.year || '')} accessed={data.accessed_date} url="https://www.ontario.ca/page/public-sector-salary-disclosure" methodology={data.methodology} />
            </div>
          )}
          </MoreDetails>
        </div>
      </section>

      {/* FAQ + one clear next step */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14" data-testid="sunshine-faq-section">
        <SectionHeader kicker="PLAIN ANSWERS" title="Common questions" />
        <FaqList items={FAQS} testId="sunshine-faq" />
      </section>

      <NextStepBand
        title="Keep going: taxes, spending, and value, all with sources."
        buttonLabel="Explore the Data"
        to="/city-data"
        secondaryLabel="Or read the CAO Review"
        secondaryTo="/cao-review"
        testId="sunshine-next-step"
      />
    </div>
  );
}
